import type { TechniqueEntry } from '../schema';

// review: candidate draft grounded in the NIAAA CBI manual — no shipped delivery prose in the
// repo; needs genuine clinical origination review, not just sign-off. The three rehearsal lines
// are member-side (what the member practises saying, never Sona's script) — the member's own
// version always wins.
export const behaviouralRehearsal: TechniqueEntry = {
  key: 'behavioural_rehearsal',
  version: '0.1-candidate',
  name: 'Behavioural rehearsal',
  pillar: 'CBT',
  serves:
    'Practising a specific upcoming situation before it happens (refusal lines, exit moves, opening sentences) so the plan exists as rehearsed behaviour, not intention.',
  method: [
    "Pick the scene from their actual calendar, never a hypothetical: the manual builds the practice from the member's specifics (who might offer, where, who else is present) because realism is what makes practice transfer.",
    'Call it practice, not role-play: the manual finds practice the more comfortable word for clients.',
    'Ask before telling: elicit their own refusal ideas and past successful refusals first: the manual notes that clients usually have very good ideas about what would work for them, and they have to own the strategy.',
    "Then rehearse live: Sona plays the counterpart, taking it easy at first and building toward harder rounds, mirroring the manual's escalating ladder: from a simple no, through a firmer refusal, to the firm friend-ask; the member's own version always wins:",
    { memberVoiced: 'No, thank you.' },
    { memberVoiced: "No, thanks. I really don't want a drink." },
    {
      memberVoiced:
        'This is very important to me… help me out here as a friend and stop trying to convince me.',
    },
    "Coach the manual's four points between rounds: state the response directly; vague excuses are dangerous (they leave the door open to another invitation); keep it short, clear, simple; where it fits, suggest an alternative that shuts the door on drinking while leaving it open to social activity.",
    'Broken record over fresh arguments: repeat one clear message, optionally acknowledging part of the pressure first.',
    'Debrief each round per the manual: did it stir an urge, how did refusing feel, was it effective. If confidence is low, reverse roles first: the member pressures, Sona models the refusal.',
    'End with their strongest line, in their voice, said once more.',
  ],
  never: [
    'Never before the pattern is understood: rehearsing the wrong scene trains the wrong move.',
    'Never script their lines wholesale; offer a range, they choose.',
    'Never escalate past what they can win in the room.',
    'Never let the practice end on a failed round.',
  ],
  requiresFirst: [
    {
      kind: 'conversation',
      clause:
        "The pull of the specific situation understood in the member's own words (what draws them in, and when it bites) however that surfaced: a walked episode, a descent, or their own plain telling.",
    },
    {
      kind: 'conversation',
      clause: 'A specific upcoming situation to rehearse for.',
    },
  ],
  doneWhen:
    'The member has produced their own words for the hard moment and delivered them under mild pressure at least once.',
  firstMove: null,
  exemplars: [],
  appliesTo: ['base', 'journey'],
  programs: null,
  signature: null,
  source:
    'NIAAA CBI manual §5.4 DREF (pp. 149–160 — escalating refusals, coaching points, broken record, practice construction and debrief) + §5.1 ASSN; catalog #7 (The Preview) + #44 + #45; base doc table row. No shipped prose in our repo.',
};
